import React from 'react';
import { default as ReactSelect } from 'react-select';
import type { CSSObjectWithLabel, StylesConfig, GroupBase } from 'react-select';

export type SelectOption = { value: string | number | null; label: string; isCustom?: boolean };

export const optionStyles: StylesConfig<SelectOption, false, GroupBase<SelectOption>> = {
  option: (provided, { data, isDisabled, isFocused, isSelected }) => {
    const isCustomOption = data.isCustom;
    if (isCustomOption) {
      return {
        ...provided,
        color: 'red',
        ':hover': { color: 'red', backgroundColor: 'grey' },
      };
    }
    return {
      ...provided,
      fontFamily: "'Montserrat', sans-serif",
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '13px',
      color: '#141414',
      backgroundColor: '#FFFFFF',
      textTransform: 'capitalize',
    };
  },
  singleValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    fontFamily: "'Montserrat', sans-serif",
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '143%',
    color: '#383838',
    paddingLeft: '10px',
  }),
  placeholder: (provided: CSSObjectWithLabel) => ({
    ...provided,
    fontFamily: "'Montserrat', sans-serif",
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '143%',
    color: '#383838',
    paddingLeft: '10px',
  }),
  container: (provided: CSSObjectWithLabel) => ({
    position: 'relative',
    ...provided,
    minWidth: '150px',
    width: '100%',
    ':hover': { borderColor: '#FFFFFF', boxShadow: 'none' },
  }),
  control: (provided: CSSObjectWithLabel, state: { isFocused: boolean }) => ({
    ...provided,
    cursor: 'pointer',
    borderRadius: '10px',
    minWidth: '150px',
    width: '100%',
    height: '40px',
    border: '1px solid #DCDEE0',
    borderColor: '#DCDEE0',
    boxShadow: 'none',
    ':hover': { border: '1px solid #DCDEE0', borderColor: '#DCDEE0', boxShadow: 'none' },
  }),
  indicatorSeparator: (provided: CSSObjectWithLabel) => ({ ...provided, display: 'none' }),
  dropdownIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: '#E3282A',
    ':hover': { color: '#E3282A' },
  }),
  clearIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: '#E3282A',
    ':hover': { color: '#E3282A' },
  }),
  menu: (provided: CSSObjectWithLabel) => ({ ...provided, borderRadius: '10px' }),
  menuPortal: (provided: CSSObjectWithLabel) => ({ ...provided, zIndex: '9999' }),
  valueContainer: (provided: CSSObjectWithLabel) => ({ ...provided, textTransform: 'capitalize' }),
};

const mergeStyles = (
  defaultStyles: StylesConfig<SelectOption, false, GroupBase<SelectOption>>,
  customStyles: Partial<StylesConfig<SelectOption, false, GroupBase<SelectOption>>>
) => {
  const newStyles = { ...defaultStyles };
  (Object.keys(defaultStyles) as (keyof typeof defaultStyles)[]).forEach((key) => {
    if (customStyles[key]) {
      (newStyles as Record<string, unknown>)[key] = (provided: CSSObjectWithLabel, state: unknown) => ({
        ...(defaultStyles[key] as (p: CSSObjectWithLabel, s: unknown) => CSSObjectWithLabel)(provided, state),
        ...(customStyles[key] as (p: CSSObjectWithLabel, s: unknown) => CSSObjectWithLabel)(provided, state),
      });
    }
  });
  return newStyles;
};

const groupStyles = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const groupBadgeStyles = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: '14px',
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center' as const,
};

const formatGroupLabel = (data: GroupBase<SelectOption>) => (
  <div style={groupStyles}>
    <span>{data.label}</span>
    <span style={groupBadgeStyles}>{data.options.length}</span>
  </div>
);

interface SelectProps {
  name?: string;
  optionSelected?: SelectOption | null;
  onSelected?: (val: SelectOption | null) => void;
  options?: SelectOption[];
  placeholder?: string;
  customStyles?: Partial<StylesConfig<SelectOption, false, GroupBase<SelectOption>>>;
  customOptions?: SelectOption[];
  isGrouped?: boolean;
  isVisible?: boolean;
  label?: string;
  selectContainerStyle?: React.CSSProperties;
  isClearable?: boolean;
  defaultValue?: SelectOption | null;
}

const Select = ({
  name = 'select',
  optionSelected,
  onSelected,
  options,
  placeholder = 'Тип продукта',
  customStyles = {},
  customOptions = [],
  isGrouped = false,
  isVisible = true,
  label = '',
  selectContainerStyle = {},
  isClearable = true,
  defaultValue = null,
}: SelectProps) => {
  const mergedStyles = mergeStyles(optionStyles, customStyles);
  const groupedOptions = [
    { label: 'Недавние', options: customOptions },
    { label: 'Юридического лица', options: options ?? [] },
  ];
  return (
    <div className="w-full" style={selectContainerStyle}>
      <label style={isVisible ? {} : { display: 'none' }}>{label}</label>
      <ReactSelect
        defaultValue={defaultValue}
        name={name}
        menuPortalTarget={document.body}
        styles={mergedStyles}
        options={isGrouped ? groupedOptions : options}
        isMulti={false}
        closeMenuOnSelect={true}
        hideSelectedOptions={false}
        onChange={onSelected}
        value={optionSelected}
        placeholder={placeholder}
        isSearchable={false}
        controlShouldRenderValue={true}
        formatGroupLabel={formatGroupLabel}
        isClearable={isClearable}
      />
    </div>
  );
};

export default Select;
